/**
 * FreedrIQChallenge — the Community Challenge splash + wizard + result page.
 *
 * Route: /freedriq/challenge
 *
 * Behaviour:
 *   * If no challenge is active, show a "closed / not-live yet" splash.
 *   * If active and the user has NOT submitted, show the two-section wizard
 *     (reuses the same UI patterns as FreedrIQ.js, but the technical section
 *     spans every tier — 75 questions today, 100 once Enterprise ships).
 *   * If the user has already submitted, jump straight to the leaderboard +
 *     shareable result card.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import { Button } from '../components/ui/button';
import {
  Sparkles, Trophy, Award, ArrowLeft, ArrowRight, CheckCircle2,
  Loader2, Users, Download, Share2, ClipboardCopy, Timer,
} from 'lucide-react';

const CHALLENGE_BAND = (s) => {
  if (s >= 85) return { label: 'FreezeDry Master',       color: 'text-primary' };
  if (s >= 70) return { label: 'Elite Operator',         color: 'text-primary' };
  if (s >= 50) return { label: 'Skilled Practitioner',   color: 'text-amber-600' };
  if (s >= 25) return { label: 'Rising Learner',         color: 'text-amber-600' };
  return          { label: 'Fresh Contender',            color: 'text-emerald-600' };
};

const FreedrIQChallenge = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [phase, setPhase] = useState('loading'); // loading | splash | wizard | result | closed
  const [challenge, setChallenge] = useState(null);
  const [config, setConfig] = useState(null);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [step, setStep] = useState(0);
  const [confidence, setConfidence] = useState({});
  const [technical, setTechnical] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);

  // Bootstrap — is there an active challenge? Has this user already submitted?
  useEffect(() => {
    const boot = async () => {
      try {
        const { data } = await axios.get(`${API}/freedriq/challenge/active`);
        const c = data.active;
        setChallenge(c);
        if (!c || !c.is_open) { setPhase('closed'); return; }
        // Has the current user already submitted?
        try {
          const sub = await axios.get(`${API}/freedriq/challenge/my-submission?challenge_id=${c.id}`);
          setResult({
            submission_id: sub.data.id,
            score:         sub.data.score,
            technical:     sub.data.technical,
            confidence:    sub.data.confidence,
            completed_at:  sub.data.completed_at,
          });
          await loadLeaderboard(c.id);
          setPhase('result');
        } catch (_e) {
          setPhase('splash');
        }
      } catch (_e) {
        setPhase('closed');
      }
    };
    if (user) boot();
  }, [API, user]);

  const loadLeaderboard = async (challengeId) => {
    try {
      const { data } = await axios.get(`${API}/freedriq/challenge/leaderboard?challenge_id=${challengeId}&limit=10`);
      setLeaderboard(data);
    } catch (_e) { /* leaderboard is best-effort */ }
  };

  const startWizard = async () => {
    try {
      const { data } = await axios.get(`${API}/freedriq/challenge/config?challenge_id=${challenge.id}`);
      setConfig(data);
      setSectionIdx(0);
      setStep(0);
      setConfidence({});
      setTechnical({});
      setPhase('wizard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the challenge');
    }
  };

  const sections = config?.sections || [];
  const activeSection = sections[sectionIdx];
  const questions = activeSection?.questions || [];
  const q = questions[step];
  const isConfidenceSection = activeSection?.id === 'starting_confidence';
  const bucket = isConfidenceSection ? confidence : technical;

  const totalQuestions = useMemo(() => sections.reduce((n, s) => n + (s.questions?.length || 0), 0), [sections]);
  const answeredCount = useMemo(() => {
    let n = 0;
    for (let i = 0; i < sectionIdx; i++) n += sections[i]?.questions?.length || 0;
    return n + step;
  }, [sectionIdx, step, sections]);
  const progress = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const canProceed = useMemo(() => {
    if (!q) return false;
    const v = bucket[q.id];
    if (q.type === 'multi_select') return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== null && v !== '';
  }, [q, bucket]);

  const setAnswer = (v) => {
    if (isConfidenceSection) setConfidence((c) => ({ ...c, [q.id]: v }));
    else                     setTechnical((t) => ({ ...t, [q.id]: v }));
  };

  const toggleMulti = (val) => {
    const cur = Array.isArray(bucket[q.id]) ? bucket[q.id] : [];
    const lastVal = q.options[q.options.length - 1].value;
    if (val === lastVal) { setAnswer([lastVal]); return; }
    const next = cur.filter((x) => x !== lastVal);
    if (next.includes(val)) setAnswer(next.filter((x) => x !== val));
    else                    setAnswer([...next, val]);
  };

  const goNext = () => {
    if (step < questions.length - 1) { setStep((s) => s + 1); return; }
    if (sectionIdx < sections.length - 1) {
      setSectionIdx((i) => i + 1);
      setStep(0);
      return;
    }
    submit();
  };
  const goBack = () => {
    if (step > 0) { setStep((s) => s - 1); return; }
    if (sectionIdx > 0) {
      const prev = sectionIdx - 1;
      setSectionIdx(prev);
      setStep((sections[prev]?.questions?.length || 1) - 1);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const confSection = sections.find((s) => s.id === 'starting_confidence');
      const starting_confidence = (confSection?.questions || []).map((qq) => Number(confidence[qq.id] || 0));
      const techSection = sections.find((s) => s.id === 'technical');
      const technical_answers = (techSection?.questions || [])
        .filter((qq) => technical[qq.id] !== undefined)
        .map((qq) => ({ question_id: qq.id, answer: technical[qq.id] }));
      const { data } = await axios.post(`${API}/freedriq/challenge/submit`, {
        challenge_id: challenge.id, starting_confidence, technical_answers,
      });
      setResult(data);
      await loadLeaderboard(challenge.id);
      setPhase('result');
      toast.success(`Challenge Score: ${data.score}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-background" data-testid="freedriq-challenge-page">
      <ToolSelector />
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-6">{children}</div>
    </div>
  );

  if (phase === 'loading') {
    return shell(
      <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Community Challenge…
      </div>,
    );
  }

  if (phase === 'closed') {
    return shell(
      <div className="pro-card p-8 text-center" data-testid="challenge-closed">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 text-primary mb-4">
          <Timer className="w-6 h-6" />
        </div>
        <h1 className="font-heading text-3xl font-extrabold">No active challenge right now</h1>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-md mx-auto">
          The FreezeDryIQ Community Challenge runs on-demand. Follow the FDAcademy account
          to be notified the moment the next one opens.
        </p>
        {challenge && (
          <p className="text-xs text-muted-foreground mt-3">
            Last challenge: <span className="font-bold">{challenge.name}</span>
            {challenge.participants > 0 && (
              <> · {challenge.participants} operator{challenge.participants === 1 ? '' : 's'} took part</>
            )}
          </p>
        )}
        <Link
          to="/freedriq?tier=free&phase=pre"
          className="btn-primary inline-flex items-center gap-1.5 rounded-full font-bold text-xs px-4 py-2 mt-5"
          data-testid="challenge-fallback-freedriq"
        >
          Take FreedrIQ instead <Sparkles className="w-3 h-3" />
        </Link>
      </div>,
    );
  }

  if (phase === 'splash') {
    return shell(
      <div className="pro-card p-8 border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent" data-testid="challenge-splash">
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <Trophy className="w-3 h-3" /> FreezeDryIQ · Community Challenge
        </div>
        <h1 className="font-heading text-3xl font-extrabold mt-1.5">{challenge.name}</h1>
        {challenge.description && (
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{challenge.description}</p>
        )}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="rounded-lg border border-border p-3 bg-background/60">
            <Trophy className="w-4 h-4 text-primary" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-1.5">Rank</div>
            <div className="text-xs font-bold">Global leaderboard</div>
          </div>
          <div className="rounded-lg border border-border p-3 bg-background/60">
            <Users className="w-4 h-4 text-primary" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-1.5">Participants</div>
            <div className="text-xs font-bold">{challenge.participants || 0} so far</div>
          </div>
          <div className="rounded-lg border border-border p-3 bg-background/60">
            <Sparkles className="w-4 h-4 text-primary" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-1.5">All tiers</div>
            <div className="text-xs font-bold">Free + Pro + Business</div>
          </div>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <Button onClick={startWizard} className="rounded-full font-bold" data-testid="challenge-start">
            Take the challenge <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
          </Button>
          <Link to="/freedriq?tier=free&phase=pre" className="text-xs text-muted-foreground hover:text-foreground font-semibold hover:underline">
            Not yet — just take FreedrIQ Free
          </Link>
        </div>
      </div>,
    );
  }

  if (phase === 'wizard' && q) {
    return shell(
      <div>
        <div className="mb-4">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <Trophy className="w-3 h-3" /> FreezeDryIQ · Community Challenge
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground mt-1">
            {challenge.name}
          </h1>
        </div>
        <div className="flex items-center gap-2 mb-4">
          {sections.map((s, i) => (
            <div
              key={s.id}
              className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border transition-colors ${
                i === sectionIdx
                  ? 'bg-primary text-primary-foreground border-primary'
                  : i < sectionIdx
                    ? 'bg-primary/10 text-primary border-primary/30'
                    : 'bg-muted text-muted-foreground border-transparent'
              }`}
            >
              {i < sectionIdx && <CheckCircle2 className="inline w-3 h-3 mr-1" />}
              {i + 1}. {s.title}
            </div>
          ))}
        </div>
        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden mb-6">
          <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="pro-card p-6" data-testid={`challenge-question-${q.id}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
            {activeSection.title} · Question {step + 1} of {questions.length}
          </div>
          <h2 className="font-heading text-xl font-extrabold text-foreground leading-tight">{q.title}</h2>
          {q.help && <p className="text-xs text-muted-foreground mt-1.5">{q.help}</p>}

          <div className="mt-4 space-y-2">
            {q.type === 'multiple_choice' && q.options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setAnswer(o.value)}
                data-testid={`challenge-option-${q.id}-${o.value}`}
                className={`w-full text-left rounded-lg border p-3 transition-colors text-sm ${
                  bucket[q.id] === o.value ? 'border-primary bg-primary/10 text-foreground' : 'border-border hover:border-primary/40 hover:bg-muted'
                }`}
              >
                <span className="font-bold text-primary mr-2">{o.value}.</span>{o.label}
              </button>
            ))}
            {q.type === 'multi_select' && q.options.map((o) => {
              const active = (bucket[q.id] || []).includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggleMulti(o.value)}
                  data-testid={`challenge-option-${q.id}-${o.value}`}
                  className={`w-full text-left rounded-lg border p-3 transition-colors text-sm flex items-center gap-2.5 ${
                    active ? 'border-primary bg-primary/10 text-foreground' : 'border-border hover:border-primary/40 hover:bg-muted'
                  }`}
                >
                  <span className={`h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center ${active ? 'bg-primary border-primary text-primary-foreground' : 'border-input'}`}>
                    {active && <CheckCircle2 className="w-3 h-3" />}
                  </span>
                  <span className="font-bold text-primary mr-1">{o.value}.</span>{o.label}
                </button>
              );
            })}
            {q.type === 'likert' && (
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                {q.options.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setAnswer(o.value)}
                    data-testid={`challenge-option-${q.id}-${o.value}`}
                    className={`rounded-lg border p-3 text-center transition-colors ${
                      bucket[q.id] === o.value ? 'border-primary bg-primary/10 text-foreground' : 'border-border hover:border-primary/40 hover:bg-muted'
                    }`}
                  >
                    <div className="font-heading text-lg font-extrabold">{o.value}</div>
                    <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{o.label}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center mt-6">
            <Button
              variant="outline"
              onClick={goBack}
              disabled={(sectionIdx === 0 && step === 0) || submitting}
              className="rounded-full font-bold"
              data-testid="challenge-back"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
            </Button>
            {(sectionIdx < sections.length - 1) || (step < questions.length - 1) ? (
              <Button onClick={goNext} disabled={!canProceed || submitting} className="rounded-full font-bold" data-testid="challenge-next">
                Next <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={!canProceed || submitting} className="rounded-full font-bold" data-testid="challenge-submit">
                {submitting
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Scoring…</>
                  : <>See my rank <Trophy className="w-3.5 h-3.5 ml-1.5" /></>}
              </Button>
            )}
          </div>
        </div>
      </div>,
    );
  }

  // === Result ===
  if (phase === 'result' && result) {
    const band = CHALLENGE_BAND(result.score || 0);
    const shareCardUrl = `${API}/freedriq/challenge/share-card/${result.submission_id}.png`;
    const rankTxt = leaderboard?.my_rank
      ? `#${leaderboard.my_rank} of ${leaderboard.total_participants}`
      : 'Awaiting rank';
    return shell(
      <div className="space-y-4" data-testid="challenge-result">
        {/* Hero */}
        <div className="pro-card p-6 relative overflow-hidden border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <Trophy className="w-3 h-3" /> {challenge?.name || 'Community Challenge'}
          </div>
          <div className="mt-3 flex items-baseline gap-3">
            <div className={`font-heading text-6xl font-extrabold ${band.color}`} data-testid="challenge-score">
              {result.score}
            </div>
            <div className="text-sm text-muted-foreground font-bold uppercase tracking-widest">/ 100</div>
          </div>
          <div className="text-lg font-heading font-extrabold text-foreground mt-1">{band.label}</div>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-1.5 font-heading font-extrabold text-sm" data-testid="challenge-rank">
            <Trophy className="w-3.5 h-3.5" /> {rankTxt}
          </div>
        </div>

        {/* Share card */}
        <div className="pro-card p-4" data-testid="challenge-share">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5 mb-2">
            <Share2 className="w-3 h-3" /> Share your rank
          </div>
          <img
            src={shareCardUrl}
            alt="Challenge share card"
            className="w-full rounded-lg border border-border"
            loading="lazy"
          />
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <a
              href={shareCardUrl}
              download={`freezedryiq-challenge-${result.submission_id}.png`}
              className="btn-primary inline-flex items-center gap-1.5 rounded-full font-bold text-xs px-3 py-2"
              data-testid="challenge-share-download"
            >
              <Download className="w-3.5 h-3.5" /> Download PNG
            </a>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(shareCardUrl);
                toast.success('Share link copied');
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-border font-bold text-xs px-3 py-2 hover:bg-muted"
              data-testid="challenge-share-copy"
            >
              <ClipboardCopy className="w-3.5 h-3.5" /> Copy image link
            </button>
          </div>
        </div>

        {/* Leaderboard */}
        {leaderboard && (
          <div className="pro-card p-6" data-testid="challenge-leaderboard">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow text-primary inline-flex items-center gap-1.5">
                <Award className="w-3 h-3" /> Top 10
              </div>
              <span className="text-[11px] text-muted-foreground">{leaderboard.total_participants} operators ranked</span>
            </div>
            <div className="space-y-1.5">
              {(leaderboard.top || []).map((row) => (
                <div
                  key={row.user_id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border ${
                    row.user_id === user?.id ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  data-testid={`challenge-lb-row-${row.rank}`}
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center font-heading font-extrabold text-sm ${
                    row.rank === 1 ? 'bg-amber-500 text-white'
                    : row.rank === 2 ? 'bg-slate-300 text-slate-800'
                    : row.rank === 3 ? 'bg-amber-700 text-white'
                    : 'bg-muted text-foreground'
                  }`}>
                    {row.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold text-sm truncate">{row.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{row.role || 'free'}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-extrabold text-lg">{row.score}</div>
                    <div className="text-[10px] text-muted-foreground">{row.correct}/{row.total} correct</div>
                  </div>
                </div>
              ))}
            </div>
            {leaderboard.my_rank && leaderboard.my_rank > (leaderboard.top?.length || 0) && leaderboard.me && (
              <>
                <div className="my-3 text-center text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                  · · · · your rank · · · ·
                </div>
                <div className="flex items-center gap-3 p-2.5 rounded-lg border border-primary bg-primary/5" data-testid="challenge-lb-me">
                  <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading font-extrabold text-sm">
                    {leaderboard.my_rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold text-sm truncate">{leaderboard.me.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{leaderboard.me.role || 'free'}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-extrabold text-lg">{leaderboard.me.score}</div>
                    <div className="text-[10px] text-muted-foreground">{leaderboard.me.correct}/{leaderboard.me.total} correct</div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-full font-bold" data-testid="challenge-back-to-dashboard">
            Back to Dashboard
          </Button>
          <Button onClick={startWizard} className="rounded-full font-bold" data-testid="challenge-retake">
            Retake and improve <Sparkles className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </div>,
    );
  }

  return shell(null);
};

export default FreedrIQChallenge;
